import { API_BASE } from '../shared/Constants';

const LS_KEY = 'maritaca_google_ads_click';

let _gtagLoaded = false;

function ensureGtagLoaded(): void {
  if (_gtagLoaded) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const sendTo = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || '';
  if (!sendTo) return;
  const adsId = sendTo.split('/')[0]; // 'AW-1234567890/abc' -> 'AW-1234567890'
  if (!adsId) return;
  // Avoid re-injecting if a previous app instance already added the tag
  if (document.querySelector('script[data-google-ads-loader="1"]')) {
    _gtagLoaded = true;
    return;
  }
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(adsId);
  // crossorigin so an error thrown inside gtag's cross-origin code reaches
  // window.onerror with a real message/stack instead of a masked
  // "Script error." (url=undefined). Google serves gtag/js with
  // Access-Control-Allow-Origin: *. See CHAT-MARITACA-10 diagnosability.
  s.crossOrigin = 'anonymous';
  s.setAttribute('data-google-ads-loader', '1');
  document.head.appendChild(s);
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };
  w.gtag('js', new Date());
  w.gtag('config', adsId);
  _gtagLoaded = true;
}
const LS_FIRED = 'maritaca_google_ads_fired';

type GoogleAdsClick = {
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  captured_at: string;
  attributed?: boolean;
};

function readClick(): GoogleAdsClick | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as GoogleAdsClick) : null;
  } catch {
    return null;
  }
}

function writeClick(c: GoogleAdsClick): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(c));
  } catch {
    /* quota errors non-fatal */
  }
}

function clean(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim();
  return s.length ? s : null;
}

/**
 * Reads gclid/gbraid/wbraid from the URL into localStorage. First-touch wins —
 * if a click is already stored we leave it alone (Google's recommended attribution).
 * Strips the params from the URL after capture so subsequent navigations are clean.
 */
export function captureGoogleAdsParamsFromUrl(): void {
  ensureGtagLoaded();
  const params = new URLSearchParams(window.location.search);
  const gclid = clean(params.get('gclid'));
  const gbraid = clean(params.get('gbraid'));
  const wbraid = clean(params.get('wbraid'));
  if (!gclid && !gbraid && !wbraid) return;

  // Strip from URL
  let dirty = false;
  for (const k of ['gclid', 'gbraid', 'wbraid']) {
    if (params.has(k)) {
      params.delete(k);
      dirty = true;
    }
  }
  if (dirty) {
    const qs = params.toString();
    const cleanUrl =
      window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  const existing = readClick();
  if (existing) return; // first-touch wins

  writeClick({
    gclid,
    gbraid,
    wbraid,
    captured_at: new Date().toISOString(),
  });
}

/**
 * Sends the persisted click identifiers to the backend so they can be linked
 * to the authenticated user, enabling server-side conversion uploads when
 * the user later subscribes (Stripe webhook -> Google Ads API).
 */
export async function attributeGoogleAdsClickToAuthedUser(
  getToken: () => Promise<string>,
): Promise<void> {
  const stored = readClick();
  if (!stored || stored.attributed) return;
  // Need at least one identifier to bother
  if (!stored.gclid && !stored.gbraid && !stored.wbraid) return;
  try {
    const token = await getToken();
    if (!token) return;
    const res = await fetch(API_BASE + '/api/google-ads/click', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Auth ' + token,
      },
      body: JSON.stringify({
        gclid: stored.gclid ?? null,
        gbraid: stored.gbraid ?? null,
        wbraid: stored.wbraid ?? null,
      }),
    });
    if (res.ok) {
      writeClick({ ...stored, attributed: true });
    }
  } catch {
    /* non-fatal */
  }
}

type ConversionPayload = {
  transaction_id: string;
  value: number;
  currency: string;
};

/**
 * Fires the gtag conversion event on Google's end (client-side complement
 * to the server-side upload from the Stripe webhook). Google deduplicates
 * by transaction_id, so firing both is the recommended belt-and-suspenders.
 *
 * Reads the conversion target from REACT_APP_GOOGLE_ADS_CONVERSION_ID
 * (e.g. 'AW-1234567890/abc123XYZ'). If not configured, becomes a no-op.
 *
 * Idempotent per transaction_id within a single browser session.
 */
export function fireGoogleAdsConversion(p: ConversionPayload): void {
  const sendTo = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || '';
  if (!sendTo) return;
  ensureGtagLoaded();

  // Dedupe per transaction within the page lifecycle
  try {
    const fired = JSON.parse(localStorage.getItem(LS_FIRED) || '[]') as string[];
    if (fired.includes(p.transaction_id)) return;
    fired.push(p.transaction_id);
    localStorage.setItem(LS_FIRED, JSON.stringify(fired.slice(-50)));
  } catch {
    /* if storage broken, allow firing — Google still dedupes server-side via transaction_id */
  }

  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag !== 'function') return;
  w.gtag('event', 'conversion', {
    send_to: sendTo,
    value: p.value,
    currency: p.currency,
    transaction_id: p.transaction_id,
  });
}

// Expose on window for E2E testing and use from non-imported call sites
if (typeof window !== 'undefined') {
  (window as unknown as { fireGoogleAdsConversion?: typeof fireGoogleAdsConversion }).fireGoogleAdsConversion =
    fireGoogleAdsConversion;
}
